import { Button, Card, Flex, PageGridCell, PageGridRow, Tag, Text } from "@wayflyer/flyui";
import type { UseUploadManagerReturn } from "@wayflyer/flyui";
import { RhfDropdownField } from "@wayflyer/flyui-hook-form";
import { useIntl } from "react-intl";

import { AuOpenBankingBanner } from "../../../../components/bank-controller-next/au-open-banking-banner";
import styles from "../../../../components/bank-controller-next/bank-controller-next.module.css";
import { UPLOAD_STATEMENTS_SIZE_LIMIT_MB } from "../../../../components/constants";
import type { useBankControllerContext } from "../../hooks/bank-controller-context";
import type { BankStatementUpload } from "../../hooks/use-bank-accounts-upload-manager";
import { AddStatementsButton } from "../bank-statements/add-statements-button";
import { MESSAGES } from "../message-definitions";

type BankAccountsEmptyStateProps = Pick<
  ReturnType<typeof useBankControllerContext>,
  | "bankConnectProps"
  | "countryForm"
  | "countryOptions"
  | "defaultCountry"
  | "isConnectionUnsupportedCountry"
  | "isConnecting"
  | "ready"
  | "requireConnection"
> & {
  isUploadDisabledPendingConnection: boolean;
  isStatementsFullScreenDropzoneEnabled: boolean;
  onConnect: () => void;
  onFilesAdded: UseUploadManagerReturn<BankStatementUpload>["onFilesAdded"];
};

export const BankAccountsEmptyState = ({
  bankConnectProps,
  countryForm,
  countryOptions,
  defaultCountry,
  isConnectionUnsupportedCountry,
  isConnecting,
  ready,
  requireConnection,
  isUploadDisabledPendingConnection,
  isStatementsFullScreenDropzoneEnabled,
  onConnect,
  onFilesAdded,
}: BankAccountsEmptyStateProps) => {
  const intl = useIntl();

  // eslint-disable-next-line react-hooks/incompatible-library -- Existing violation after eslint-plugin-react-hooks v7 upgrade; remove when fixed.
  const selectedCountryCode = countryForm.watch("country");

  const pageLayout = {
    connectionCard: { md: { span: 6 }, sm: { span: 5 }, xxs: { span: 6 } },
    uploadCard: { md: { span: 4 }, sm: { span: 3 }, xxs: { span: 6 } },
  };

  return (
    <PageGridRow stretch>
      <PageGridCell layout={pageLayout.connectionCard}>
        <Card
          border="gradient"
          slots={{
            header: (
              <Card.Header
                title={intl.formatMessage(MESSAGES.emptyStateConnectionCardTitle)}
                subtitle={intl.formatMessage(MESSAGES.emptyStateConnectionCardSubtitle)}
                adornment={
                  <Tag
                    content={intl.formatMessage(
                      requireConnection ? MESSAGES.emptyStateRequiredBadge : MESSAGES.emptyStateRecommendedBadge
                    )}
                    color="info"
                    variant="default"
                  />
                }
              />
            ),
          }}
        >
          <Flex direction="column" gap="4">
            <RhfDropdownField
              name="country"
              initialValue={defaultCountry?.code}
              options={countryOptions}
              form={countryForm}
              label={intl.formatMessage(MESSAGES.bankLocationLabel)}
            />
            {bankConnectProps.basiqConnectProps.openBankingEnabled && selectedCountryCode === "AU" && (
              <AuOpenBankingBanner />
            )}
            <Button
              variant="Secondary"
              fullWidth
              onClick={onConnect}
              disabled={isConnectionUnsupportedCountry}
              loading={!ready || isConnecting}
            >
              {intl.formatMessage(MESSAGES.addAccountInstantly)}
            </Button>
          </Flex>
        </Card>
      </PageGridCell>

      <PageGridCell layout={pageLayout.uploadCard}>
        <Flex direction="column" gap="6" width="100%" height="100%" minHeight="180px">
          <Flex direction="column" grow={1} minHeight="0" width="100%" height="100%" className={styles.uploadCardFill}>
            <Card
              disabled={isUploadDisabledPendingConnection}
              slots={{
                header: (
                  <Card.Header
                    title={intl.formatMessage(MESSAGES.emptyStateUploadCardTitle)}
                    subtitle={intl.formatMessage(MESSAGES.emptyStateUploadCardSubtitle, {
                      sizeLimit: UPLOAD_STATEMENTS_SIZE_LIMIT_MB,
                    })}
                  />
                ),
              }}
            >
              <Flex height="100%" align="flex-end" width="100%">
                <AddStatementsButton
                  onFilesAdded={onFilesAdded}
                  disabled={isUploadDisabledPendingConnection}
                  fullScreenDropzone={isStatementsFullScreenDropzoneEnabled}
                  fullWidth
                />
              </Flex>
            </Card>
          </Flex>
          {isUploadDisabledPendingConnection && (
            <Flex width="100%" justify="center" shrink={0}>
              <Text size="sm" color="placeholder">
                {intl.formatMessage(MESSAGES.uploadAvailableAfterBankConnection)}
              </Text>
            </Flex>
          )}
        </Flex>
      </PageGridCell>
    </PageGridRow>
  );
};
